import { useState, type RefObject } from 'react';
import { isPdfDocument } from '@apis/documents';
import { useDocumentBlob } from '@components/Pages/DocumentsPage/hooks/useDocumentBlob';
import { useObjectUrl } from '@hooks/useObjectUrl';
import { usePdfDocument } from '@hooks/usePdfDocument';
import { usePdfPageCanvas } from './usePdfPageCanvas';
import { useDocumentZoom } from './useDocumentZoom';

/**
 * The document on screen: its bytes, which page, how big, and whether it has
 * finished rendering — an `<img>` that fires `load` and a pdf canvas answering
 * the same questions. `contentReady` gates drawing: a mark anchored to a rect
 * that is about to change lands in the wrong place.
 */
export function useDocumentContent(
  siteId: string,
  fileId: string,
  docName: string,
  /** The viewer box zoom measures against. */
  viewerRef: RefObject<HTMLDivElement | null>,
) {
  const isPdf = isPdfDocument({ name: docName, id: fileId });

  // The same per-file cache the table's preview warms — no second download.
  const { data: blob = null, isError: loadFailed } = useDocumentBlob(siteId, fileId, !!fileId);
  const imageUrl = useObjectUrl(isPdf ? null : blob);
  const [loadedImageUrl, setLoadedImageUrl] = useState<string | null>(null);

  const { zoom, setZoomPct, applyZoom, initZoom, handleImageLoad, contentNatural, imageZoomStyle } =
    useDocumentZoom(viewerRef);
  const pdfDoc = usePdfDocument(isPdf ? blob : null);
  const { page, setPage, canvasRef, renderedPage } = usePdfPageCanvas(pdfDoc, isPdf ? zoom : null, initZoom);

  return {
    isPdf,
    blob,
    imageUrl,
    imageZoomStyle,
    canvasRef,
    /** The image reports its own size once decoded; the zoom hook needs both. */
    onImageLoad: (naturalWidth: number, naturalHeight: number, url: string) => {
      handleImageLoad(naturalWidth, naturalHeight);
      setLoadedImageUrl(url);
    },

    page: isPdf ? page : 1,
    pageCount: isPdf ? pdfDoc.numPages : 1,
    setPage,

    zoom,
    setZoomPct,
    applyZoom,
    contentNatural,

    /** Re-measures the overlay when it changes: a page flip resizes the canvas. */
    contentKey: isPdf ? renderedPage : (imageUrl ?? ''),
    contentReady: isPdf ? renderedPage > 0 : imageUrl !== null && loadedImageUrl === imageUrl,
    failed: loadFailed || pdfDoc.error,
  };
}
